import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { authHeaders } from "./attachmentUtils";

/**
 * Renderiza PDF en canvas (pdf.js) — funciona dentro del CRM sin blob: ni iframe al API.
 */
export default function PdfCanvasPreview({ attachmentId, filename, colors }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!attachmentId) return undefined;

    let cancelled = false;
    const container = containerRef.current;
    if (!container) return undefined;

    const render = async () => {
      setLoading(true);
      setError(false);
      container.innerHTML = "";

      try {
        const apiBase = process.env.REACT_APP_API_URL;
        const res = await fetch(
          `${apiBase}/emails/attachment/${encodeURIComponent(String(attachmentId ?? ""))}/preview`,
          { headers: authHeaders() }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.arrayBuffer();

        const pdfjs = await import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) return;

        const maxWidth = container.clientWidth || 720;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });
          const scale = Math.min(1.6, maxWidth / viewport.width);
          const scaled = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = scaled.width;
          canvas.height = scaled.height;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 12px";
          canvas.style.maxWidth = "100%";
          canvas.style.height = "auto";
          canvas.style.borderRadius = "6px";
          canvas.style.boxShadow = "0 1px 4px rgba(0,0,0,0.12)";
          canvas.style.background = "#fff";

          container.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport: scaled,
          }).promise;
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    render();

    return () => {
      cancelled = true;
      if (container) container.innerHTML = "";
    };
  }, [attachmentId, filename]);

  const c = colors || {};

  if (error) {
    return (
      <Box sx={{ textAlign: "center", px: 2, py: 4 }}>
        <Typography sx={{ fontWeight: 700, color: c.textPrimary }}>
          No se pudo renderizar el PDF
        </Typography>
        <Typography sx={{ fontSize: 12, color: c.textMuted, mt: 0.5 }}>
          Usa «Abrir en pestaña» o «Descargar».
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: 200 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: c.accent }} />
        </Box>
      ) : null}
      <Box ref={containerRef} sx={{ width: "100%" }} />
    </Box>
  );
}
